import type {ApiObject, ApiOptions, LoadOptions} from '@rudderstack/analytics-js/bundled';
import {RudderAnalytics} from '@rudderstack/analytics-js/bundled';
import type {AnalyticsPlugin} from 'analytics';

import type {GetSourceConfig, RudderstackConfig, RudderstackLoadOptions} from '../shared/types';

export default function rudderstackEvents(config: RudderstackConfig): AnalyticsPlugin {
  let rudderInstance: InstanceType<typeof RudderAnalytics>;

  return {
    name: 'rudderstackEvents',
    initialize: () => {
      // RudderStack is a browser-only SDK — skip initialization in SSR environments.
      if (typeof window === 'undefined') return;
      rudderInstance = new RudderAnalytics();
      rudderInstance.load(
        config.writeKey,
        config.dataPlaneUrl,
        buildLoadOptions(config) as LoadOptions,
      );
    },
    page: ({payload}: {payload: Record<string, unknown>}) => {
      const {properties} = (payload.properties ?? {}) as Record<string, unknown>;
      const options = (payload.context ?? {}) as ApiOptions;
      rudderInstance.page((properties ?? {}) as ApiObject, options);
    },
    track: ({payload}: {payload: Record<string, unknown>}) => {
      const event = payload.event as string;
      // Only governed events in the tracking plan may be tracked for now
      if (event !== 'element:clicked') {
        throw new Error(`Unplanned track event: "${event}".`);
      }
      const properties = (payload.properties ?? {}) as ApiObject;
      const options = (payload.context ?? {}) as ApiOptions;
      rudderInstance.track(event, properties, options);
    },
  } as AnalyticsPlugin;
}

function buildLoadOptions(config: RudderstackConfig): RudderstackLoadOptions | undefined {
  const {sourceName, sourceId, workspaceId, writeKey, loadOptions} = config;
  if (sourceName && sourceId && workspaceId) {
    return {
      getSourceConfig: () => buildSourceConfig(sourceName, sourceId, workspaceId, writeKey),
      ...loadOptions,
    };
  }
  return loadOptions;
}

function buildSourceConfig(
  sourceName: string,
  sourceId: string,
  workspaceId: string,
  writeKey: string,
): GetSourceConfig {
  return {
    updatedAt: new Date().toISOString(),
    source: {
      name: sourceName,
      id: sourceId,
      workspaceId,
      writeKey,
      updatedAt: new Date().toISOString(),
      enabled: true,
      config: {
        statsCollection: {
          errors: {enabled: false},
          metrics: {enabled: false},
        },
      },
      destinations: [],
    },
  };
}
